import { useState } from "react";
import axios from "axios";
import FileSaver from "file-saver";
import { Button } from "components/core/basic/Button";
import getURLPrefix from "utils/getUrlPrefix";
import { FaRegFileExcel } from "react-icons/fa6";
import { type UseMutationResult } from "@tanstack/react-query";

const URL_PREFIX = getURLPrefix();

interface Props {
  url_path: string;
  filters: Record<string, unknown>;
  /**
   * This param is probably deprecated, don't see mentions of it in the backend code
   */
  report_dl?: string;
  buttonText?: string;
  endpoint?: string;
  type?: string;
}

const DownloadReport = ({
  url_path,
  filters,
  report_dl = "",
  buttonText = "Download Report",
  endpoint = "/api/data_report/",
  type = "xls",
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    let url = URL_PREFIX + `${endpoint}${url_path}`;

    const params = JSON.parse(JSON.stringify(filters)) as Record<string, unknown>;
    params["xls"] = "true";
    params["report_dl"] = report_dl;

    let queryString = "";
    for (const [key, value] of Object.entries(params)) {
      if (value === null) {
        continue;
      }
      if (queryString.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        queryString += `${key}=${value?.toString()}`;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        queryString += `&${key}=${value?.toString()}`;
      }
    }
    url = `${url}?${queryString}`;
    setIsLoading(true);

    if (type === "xls") {
      const response = await axios({
        method: "GET",
        url: url,
        responseType: "blob",
      });

      setIsLoading(false);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
      });
      FileSaver.saveAs(blob, "report.xlsx");
      return;
    }
    const response = await axios({
      method: "GET",
      url: url,
    });

    setIsLoading(false);
    // For CSV files
    const blob = new Blob([response.data], {
      type: "text/csv",
    });

    FileSaver.saveAs(blob, "report.csv");
  };

  return (
    <Button isPending={isLoading} onPress={handleExport} marginTop="1rem">
      <FaRegFileExcel /> {!isLoading ? buttonText : "Downloading..."}
    </Button>
  );
};

export default DownloadReport;

export const DownloadV4Report = ({
  downloadMutation,
  children = "Download Report",
  fileName = "report.xlsx",
}: {
  downloadMutation: UseMutationResult<Blob, unknown, void, unknown>;
  children?: React.ReactNode;
  fileName?: string;
}) => {
  return (
    <Button
      isPending={downloadMutation.isPending}
      onPress={() =>
        downloadMutation.mutate(undefined, {
          onSuccess: (data) => {
            FileSaver.saveAs(data, fileName);
          },
        })
      }
    >
      <FaRegFileExcel /> {!downloadMutation.isPending ? children : "Downloading..."}
    </Button>
  );
};
